import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { UploadCloud, Search, Subtitles, Trash2 } from "lucide-react";

export default function AdminUpload() {
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [tmdbConfigured, setTmdbConfigured] = useState(false);
  const [tmdbQuery, setTmdbQuery] = useState("");
  const [tmdbResults, setTmdbResults] = useState([]);
  const [searchingTmdb, setSearchingTmdb] = useState(false);
  const [createdMovieId, setCreatedMovieId] = useState(null);
  const [subs, setSubs] = useState([]);
  const [subFile, setSubFile] = useState(null);
  const [subLang, setSubLang] = useState("en");
  const [subLabel, setSubLabel] = useState("English");
  const [form, setForm] = useState({
    title: "", description: "", year: 2024, duration_minutes: 0, rating: "NR",
    genres: "", cast: "", director: "", poster_url: "", backdrop_url: "", featured: false, tmdb_id: "",
  });

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/settings").then(({ data }) => setTmdbConfigured(data.tmdb_configured)).catch(() => {});
  }, []);

  const searchTmdb = async () => {
    if (!tmdbQuery.trim()) return;
    setSearchingTmdb(true);
    try {
      const { data } = await api.get(`/tmdb/search?q=${encodeURIComponent(tmdbQuery)}`);
      setTmdbResults(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "TMDB search failed");
    } finally { setSearchingTmdb(false); }
  };

  const pickTmdb = async (r) => {
    try {
      const { data } = await api.get(`/tmdb/movie/${r.tmdb_id}`);
      setForm({
        title: data.title || "",
        description: data.description || "",
        year: data.year || 2024,
        duration_minutes: data.duration_minutes || 0,
        rating: data.rating || "NR",
        genres: (data.genres || []).join(", "),
        cast: (data.cast || []).join(", "),
        director: data.director || "",
        poster_url: data.poster_url || "",
        backdrop_url: data.backdrop_url || "",
        featured: false,
        tmdb_id: data.tmdb_id || "",
      });
      setTmdbResults([]);
      setTmdbQuery("");
      toast.success("Metadata filled from TMDB");
    } catch (err) {
      toast.error("Could not fetch movie details");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a video file"); return; }
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSubmitting(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (k === "tmdb_id" && !v) return;
      fd.append(k, v);
    });
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload/video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => { if (p.total) setProgress(Math.round((p.loaded / p.total) * 100)); },
      });
      toast.success("Movie uploaded");
      setCreatedMovieId(data.id);
      loadSubs(data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setSubmitting(false); }
  };

  const loadSubs = async (movieId) => {
    const { data } = await api.get(`/movies/${movieId}/subtitles`);
    setSubs(data);
  };

  const uploadSub = async (e) => {
    e.preventDefault();
    if (!subFile || !createdMovieId) return;
    const fd = new FormData();
    fd.append("language", subLang);
    fd.append("label", subLabel);
    fd.append("is_default", subs.length === 0);
    fd.append("file", subFile);
    try {
      await api.post(`/movies/${createdMovieId}/subtitles`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Subtitle added");
      setSubFile(null);
      loadSubs(createdMovieId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Subtitle upload failed");
    }
  };

  const deleteSub = async (sid) => {
    await api.delete(`/subtitles/${sid}`);
    loadSubs(createdMovieId);
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-32 pb-24" data-testid="admin-upload-page">
      <div className="px-6 md:px-12 max-w-3xl mx-auto">
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#D9381E]">Library</span>
        <h1 className="font-display text-5xl font-black tracking-tighter text-white mt-3">Upload Film</h1>

        {/* TMDB search box */}
        {tmdbConfigured && !createdMovieId && (
          <div className="mt-10 border border-[#222] p-5" data-testid="tmdb-search-section">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D9381E] mb-3">
              <Search size={12} strokeWidth={1.5} /> Auto-fill from TMDB
            </div>
            <div className="flex gap-2">
              <input value={tmdbQuery} onChange={(e) => setTmdbQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchTmdb())}
                placeholder="Search by title…"
                className="flex-1 bg-[#0F0F0F] border border-[#222] focus:border-[#D9381E] focus:outline-none text-white px-4 py-3"
                data-testid="tmdb-query-input" />
              <button type="button" onClick={searchTmdb} disabled={searchingTmdb}
                className="bg-[#D9381E] hover:bg-[#ED4B32] disabled:opacity-50 text-white px-5 py-3 text-xs uppercase tracking-[0.2em]"
                data-testid="tmdb-search-button">{searchingTmdb ? "…" : "Search"}</button>
            </div>
            {tmdbResults.length > 0 && (
              <div className="mt-4 max-h-72 overflow-y-auto border border-[#222]">
                {tmdbResults.map((r) => (
                  <button type="button" key={r.tmdb_id} onClick={() => pickTmdb(r)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-[#0F0F0F] text-left border-b border-[#222] last:border-b-0 transition-colors"
                    data-testid={`tmdb-result-${r.tmdb_id}`}>
                    {r.poster_url ? <img src={r.poster_url} alt="" className="w-10 h-14 object-cover" /> : <div className="w-10 h-14 bg-[#1A1A1A]" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-white truncate">{r.title} {r.year && <span className="text-[#8A8A8A]">({r.year})</span>}</p>
                      <p className="text-xs text-[#8A8A8A] line-clamp-1">{r.overview}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {!tmdbConfigured && (
          <p className="mt-8 text-xs text-[#8A8A8A]">
            <span className="text-[#fcd34d]">●</span> Configure TMDB in <a href="/admin/settings" className="text-[#D9381E] hover:text-[#ED4B32]">Settings</a> for auto-fill.
          </p>
        )}

        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="upload-form">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">Video file (MP4)</span>
            <div className="mt-2 border border-dashed border-[#333] hover:border-[#D9381E] transition-colors p-8 text-center">
              <UploadCloud className="mx-auto text-[#8A8A8A]" size={28} strokeWidth={1.5} />
              <input type="file" accept="video/*" onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-3 block w-full text-sm text-[#C8C8C8] file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#D9381E] file:text-white file:cursor-pointer"
                data-testid="upload-file-input" />
              {file && <p className="mt-2 text-xs text-[#8A8A8A]">{file.name} · {(file.size / (1024*1024)).toFixed(1)} MB</p>}
            </div>
          </label>

          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Title" required value={form.title} onChange={(v) => upd("title", v)} testid="upload-title" />
            <Field label="Director" value={form.director} onChange={(v) => upd("director", v)} testid="upload-director" />
            <Field label="Year" type="number" value={form.year} onChange={(v) => upd("year", Number(v) || 0)} testid="upload-year" />
            <Field label="Duration (min)" type="number" value={form.duration_minutes} onChange={(v) => upd("duration_minutes", Number(v) || 0)} testid="upload-duration" />
            <Field label="Rating" value={form.rating} onChange={(v) => upd("rating", v)} testid="upload-rating" />
            <Field label="Genres (comma sep)" value={form.genres} onChange={(v) => upd("genres", v)} testid="upload-genres" />
            <Field label="Cast (comma sep)" value={form.cast} onChange={(v) => upd("cast", v)} testid="upload-cast" />
            <Field label="Poster URL" value={form.poster_url} onChange={(v) => upd("poster_url", v)} testid="upload-poster" />
            <Field label="Backdrop URL" value={form.backdrop_url} onChange={(v) => upd("backdrop_url", v)} testid="upload-backdrop" full />
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">Description</span>
            <textarea value={form.description} onChange={(e) => upd("description", e.target.value)} rows={4}
              className="mt-2 w-full bg-[#0F0F0F] border border-[#222] focus:border-[#D9381E] focus:outline-none text-white px-4 py-3"
              data-testid="upload-description" />
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={(e) => upd("featured", e.target.checked)} className="accent-[#D9381E]" data-testid="upload-featured" />
            <span className="text-sm text-[#C8C8C8]">Set as featured (hero banner)</span>
          </label>

          {submitting && progress > 0 && (
            <div className="border border-[#222] p-4">
              <div className="flex items-center justify-between text-xs text-[#8A8A8A] mb-2">
                <span>Uploading…</span><span>{progress}%</span>
              </div>
              <div className="h-1 bg-[#222]"><div className="h-full bg-[#D9381E] transition-all" style={{ width: `${progress}%` }} /></div>
            </div>
          )}

          <button type="submit" disabled={submitting || createdMovieId}
            className="bg-[#D9381E] hover:bg-[#ED4B32] disabled:opacity-60 text-white px-8 py-3 text-sm uppercase tracking-[0.2em]"
            data-testid="upload-submit-button">
            {submitting ? "Uploading…" : createdMovieId ? "Uploaded" : "Upload"}
          </button>
        </form>

        {/* Subtitles - only after movie created */}
        {createdMovieId && (
          <div className="mt-12 border border-[#222] p-6" data-testid="subtitles-section">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D9381E] mb-4">
              <Subtitles size={12} strokeWidth={1.5} /> Subtitles
            </div>

            {form.tmdb_id && (
              <OpenSubsSearchBox movieId={createdMovieId} tmdbId={form.tmdb_id} onAdded={() => loadSubs(createdMovieId)} />
            )}

            <p className="text-sm text-[#8A8A8A] mb-4">Upload .srt or .vtt files. SRT will be auto-converted.</p>

            <form onSubmit={uploadSub} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input value={subLang} onChange={(e) => setSubLang(e.target.value)} placeholder="Lang code (en)"
                className="bg-[#0F0F0F] border border-[#222] focus:border-[#D9381E] focus:outline-none text-white px-4 py-3 text-sm"
                data-testid="sub-lang-input" />
              <input value={subLabel} onChange={(e) => setSubLabel(e.target.value)} placeholder="Label (English)"
                className="bg-[#0F0F0F] border border-[#222] focus:border-[#D9381E] focus:outline-none text-white px-4 py-3 text-sm"
                data-testid="sub-label-input" />
              <input type="file" accept=".srt,.vtt,text/vtt" onChange={(e) => setSubFile(e.target.files?.[0] || null)}
                className="text-sm text-[#C8C8C8] file:mr-2 file:py-2 file:px-4 file:border-0 file:bg-[#222] file:text-white"
                data-testid="sub-file-input" />
              <button type="submit" disabled={!subFile} className="sm:col-span-3 bg-[#D9381E] hover:bg-[#ED4B32] disabled:opacity-50 text-white py-2 text-xs uppercase tracking-[0.2em]"
                data-testid="sub-upload-button">Upload Subtitle</button>
            </form>

            {subs.length > 0 && (
              <div className="mt-5 border border-[#222]">
                {subs.map((s) => (
                  <div key={s.id} className="flex items-center justify-between p-3 border-b border-[#222] last:border-b-0" data-testid={`sub-row-${s.id}`}>
                    <div>
                      <p className="text-white text-sm">{s.label} <span className="text-[#8A8A8A] text-xs">({s.language})</span></p>
                      {s.is_default && <p className="text-[10px] uppercase tracking-[0.2em] text-[#86efac]">Default</p>}
                    </div>
                    <button onClick={() => deleteSub(s.id)} className="text-[#8A8A8A] hover:text-[#fca5a5]" data-testid={`sub-delete-${s.id}`}>
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button onClick={() => nav("/admin")} className="mt-6 text-xs uppercase tracking-[0.2em] border border-[#222] hover:border-white text-[#8A8A8A] hover:text-white px-4 py-2 transition-colors"
              data-testid="upload-done-button">Done — back to Admin</button>
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", required, testid, full }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">{label}</span>
    <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)}
      className="mt-2 w-full bg-[#0F0F0F] border border-[#222] focus:border-[#D9381E] focus:outline-none text-white px-4 py-3"
      data-testid={testid} />
  </label>
);

const OpenSubsSearchBox = ({ movieId, tmdbId, onAdded }) => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/opensubs/search?tmdb_id=${tmdbId}&languages=en`);
      setResults(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "OpenSubs not configured or search failed");
      setResults([]);
    } finally { setLoading(false); }
  };

  const pick = async (r) => {
    try {
      await api.post("/opensubs/download", {
        file_id: r.file_id, movie_id: movieId,
        language: r.language, label: r.label || r.language.toUpperCase(),
      });
      toast.success("Subtitle attached");
      onAdded?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Download failed"); }
  };

  return (
    <div className="mb-5 border border-dashed border-[#222] p-4" data-testid="opensubs-box">
      <div className="flex items-center justify-between">
        <p className="text-sm text-white">Search OpenSubtitles for this movie</p>
        <button type="button" onClick={search} disabled={loading}
          className="text-xs uppercase tracking-[0.2em] border border-[#222] hover:border-[#D9381E] hover:text-[#D9381E] text-[#8A8A8A] px-4 py-2 transition-colors disabled:opacity-50"
          data-testid="opensubs-search-button">
          {loading ? "Searching…" : "Search"}
        </button>
      </div>
      {results && results.length === 0 && <p className="text-xs text-[#8A8A8A] mt-3">No results found.</p>}
      {results && results.length > 0 && (
        <div className="mt-3 max-h-64 overflow-y-auto border border-[#222]">
          {results.map((r) => (
            <button type="button" key={r.file_id} onClick={() => pick(r)}
              className="w-full flex items-center justify-between p-3 hover:bg-[#0F0F0F] text-left border-b border-[#222] last:border-b-0"
              data-testid={`opensubs-result-${r.file_id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm truncate">{r.label || r.release || `${r.language.toUpperCase()} subtitle`}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A]">{r.language} · {r.downloads} downloads</p>
              </div>
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#D9381E]">Add →</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
